import {
  AuthService,
  BikeIcon,
  CalendarIcon,
  CompanyIcon,
  ConsentService,
  Event,
  getCampaignFamilyConfig,
  getEventFamily,
  HomeOutlinedIcon,
  PeopleIcon,
  PersonIcon,
} from '@geovelo-frontends/commons';
import {
  AdminPanelSettingsOutlined,
  ChatOutlined,
  CookieOutlined,
  FolderOpen,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MailOutline,
  MoreHoriz,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Drawer as MuiDrawer,
  Popover,
  Skeleton,
  SvgIcon,
  SvgIconProps,
  Toolbar,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, NavLinkProps } from 'react-router';

import {
  alphaPages,
  AppContext,
  getStartedAdminSteps,
  getStartedEmployeeSteps,
  TPageKey,
} from '../../app/context';
import { toggleReactQueryDevtools } from '../../app/devtools-toggle';
import {
  AlphaBadge,
  AppInlineIcon,
  CertificatesIcon,
  ChevronRightIcon,
  ConfigIcon,
  CupIcon,
  EditIcon,
  FMDIcon,
  HelpIcon,
  HighlightedBadge,
  LeaderboardIcon,
  LogoutIcon,
  ParticipantsIcon,
  PremiumAvatar,
  PremiumIcon,
  StatsIcon,
  SurveyIcon,
} from '../../components';
import ContactAdminDialog from '../../components/contact-admin-dialog';
import UpgradeOfferButton from '../../components/upgrade-offer-button';
import { environment } from '../../environment';
import useCrisp from '../../hooks/crisp';
import usePageVisibility from '../../hooks/page-visibility';
import useContactAdmin from '../../hooks/use-contact-admin';
import useHighlightedEventsQuery from '../../hooks/use-highlighted-events-query';
import usePartnerFMDConfigQuery from '../../hooks/use-partner-fmd-config-query';
import usePartnerGeogroupQuery from '../../hooks/use-partner-geogroup-query';
import RestrictAccessDialog from '../../pages/admin/employees/restrict-access-dialog';
import { featureFlags } from '../../utils/feature-flags';

import CompanyMenu from './company-menu';

export const drawerWidth = 280;
// Le content doit garder au moins 600px : on bascule la sidebar en mode
// "bouton" (drawer temporaire) dès que la largeur totale de la page ne
// permet plus d'afficher la sidebar fixe (280px) + 600px de content.
const contentMinWidth = 600;
export const drawerBreakpoint = contentMinWidth + drawerWidth;

function Drawer({
  openOnMobile,
  toggle,
}: {
  openOnMobile: boolean;
  toggle: () => void;
}): React.JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.up(drawerBreakpoint)]: {
          flexShrink: 0,
          width: drawerWidth,
        },
      })}
    >
      <MuiDrawer
        anchor="top"
        ModalProps={{
          keepMounted: true,
        }}
        onClose={toggle}
        open={openOnMobile}
        sx={(theme) => ({
          display: 'block',
          [theme.breakpoints.up(drawerBreakpoint)]: { display: 'none' },
          '& .MuiDrawer-paper': {
            backgroundColor: '#fff',
            borderRight: 0,
            boxSizing: 'border-box',
            color: '#212121',
            height: '100%',
            width: '100%',
            [theme.breakpoints.up(drawerBreakpoint)]: { width: drawerWidth },
          },
        })}
        variant="temporary"
      >
        <DrawerContent toggle={toggle} />
      </MuiDrawer>
      <MuiDrawer
        open
        sx={(theme) => ({
          display: 'none',
          [theme.breakpoints.up(drawerBreakpoint)]: { display: 'block' },
          '& .MuiDrawer-paper': {
            backgroundColor: '#fff',
            borderRight: '1px solid #E3E7EE',
            boxSizing: 'border-box',
            color: '#212121',
            width: drawerWidth,
          },
        })}
        variant="permanent"
      >
        <DrawerContent />
      </MuiDrawer>
    </Box>
  );
}

const linkLists: Array<{
  key: string;
  items: Array<{
    Icon: ((props: SvgIconProps) => React.JSX.Element) | typeof SvgIcon;
    key: TPageKey;
    titleKey: string;
    to: string;
  }>;
  titleKey: string;
}> = [
  {
    key: 'animation',
    titleKey: 'companies.navigation.animation',
    items: [
      {
        key: 'challenges',
        to: '/challenges',
        titleKey: 'companies.navigation.challenges',
        Icon: CupIcon,
      },
      {
        key: 'leaderboards',
        to: '/leaderboards',
        titleKey: 'companies.navigation.leaderboards',
        Icon: LeaderboardIcon,
      },
      {
        key: 'news',
        to: '/news',
        titleKey: 'companies.navigation.news',
        Icon: CertificatesIcon,
      },
      {
        key: 'covelotaf',
        to: '/covelotaf',
        titleKey: 'companies.navigation.covelotaf',
        Icon: ParticipantsIcon,
      },
    ],
  },
  {
    key: 'fmd',
    titleKey: 'companies.navigation.fmd',
    items: [
      {
        key: 'fmdConfig',
        to: '/bicycle-compensation/config',
        titleKey: 'companies.navigation.fmd_config',
        Icon: EditIcon,
      },
      {
        key: 'fmdCertificates',
        to: '/bicycle-compensation/certificates',
        titleKey: 'companies.navigation.bicycle_compensation_certificates',
        Icon: CertificatesIcon,
      },
      {
        key: 'fmdInfo',
        to: '/bicycle-compensation/config',
        titleKey: 'companies.navigation.fmd_info',
        Icon: FMDIcon,
      },
      {
        key: 'fmd',
        to: '/bicycle-compensation',
        titleKey: 'companies.navigation.bicycle_compensation',
        Icon: CalendarIcon,
      },
      {
        key: 'fmdEmployee',
        to: '/bicycle-compensation',
        titleKey: 'companies.navigation.bicycle_compensation_employee',
        Icon: FMDIcon,
      },
    ],
  },
  {
    key: 'data',
    titleKey: 'companies.navigation.data',
    items: [
      {
        key: 'holdingOverview',
        to: '/holding',
        titleKey: 'companies.navigation.holding_overview',
        Icon: PeopleIcon,
      },
      {
        key: 'stats',
        to: '/stats',
        titleKey: 'companies.navigation.stats',
        Icon: StatsIcon,
      },
      {
        key: 'incitation',
        to: '/incitation',
        titleKey: 'companies.navigation.cycling_practice',
        Icon: BikeIcon,
      },
    ],
  },
];

function DrawerContent({ toggle }: { toggle?: () => void }): React.JSX.Element {
  const [companyMenuAnchorEl, setCompanyMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [restrictAccessDialogOpen, openRestrictAccessDialog] = useState(false);
  const [hideFmd, setHideFmd] = useState<boolean | null>();
  const {
    partner: {
      current: currentPartner,
      type: partnerType,
      permissions,
      getStartedProgression: getStartedAdminProgression,
      isFrenchGeogroup,
    },
    user: {
      current: currentUser,
      employee,
      employees,
      getStartedProgression: getStartedEmployeeProgression,
    },
    actions: { setCurrentUser },
  } = useContext(AppContext);
  const { data: fmdConfig } = usePartnerFMDConfigQuery(currentPartner, {
    isHolding: partnerType === 'holding',
  });
  const { data: geogroup } = usePartnerGeogroupQuery(currentPartner);
  const { data: highlightedEvents } = useHighlightedEventsQuery(currentPartner, geogroup, {
    includeFinished: true,
    onlyBannerEnabled: false,
  });
  const { t } = useTranslation();
  const { initialized: crispInitialized, showChat } = useCrisp();
  const { isAuthorized } = usePageVisibility();
  const { contactAdmin, adminToDisplay, closeAdminDialog } = useContactAdmin();

  useEffect(() => {
    setHideFmd(!(isFrenchGeogroup ?? true));
  }, [isFrenchGeogroup]);

  async function handleSignOut() {
    setSigningOut(true);

    try {
      await AuthService.signOut();
      setCurrentUser(null);
    } catch (err) {
      console.error(err);
      setSigningOut(false);
    }
  }

  const primaryTypographyProps: TypographyProps<'span', { component: 'div' }> = {
    alignItems: 'center',
    component: 'div',
    display: 'flex',
    gap: 1,
    variant: 'body2',
  };

  // One entry per campaign family (bfg, mav...) — the first (most recent, per the -start_datetime
  // ordering) highlighted event found for each, so both can show up at once in the drawer.
  const highlightedDrawerEvents: Event[] = [];
  const highlightedDrawerFamilies = new Set<string>();
  highlightedEvents?.forEach((event) => {
    const family = getEventFamily(event.code);
    if (
      !family ||
      highlightedDrawerFamilies.has(family) ||
      !getCampaignFamilyConfig(event.code)?.hasDrawerHighlight
    ) {
      return;
    }

    highlightedDrawerFamilies.add(family);
    highlightedDrawerEvents.push(event);
  });

  const displayGetStartedEmployeeLink =
    employee?.isCollaborator &&
    getStartedEmployeeProgression &&
    !getStartedEmployeeProgression.allDone;

  const displayGetStartedAdminLink =
    partnerType &&
    partnerType !== 'holding' &&
    employee?.isAdminOrAnimator &&
    getStartedAdminProgression &&
    !getStartedAdminProgression.allDone;

  return (
    <>
      <Toolbar sx={{ gap: 1 }}>
        {currentPartner?.icon ? (
          <Avatar src={currentPartner.icon} />
        ) : (
          <Avatar sx={({ palette }) => ({ bgcolor: palette.primary.main })}>
            <CompanyIcon />
          </Avatar>
        )}
        <Typography noWrap fontWeight={700} title={currentPartner?.title}>
          {currentPartner?.title || <Skeleton variant="text" width={100} />}
        </Typography>
        {employees && employees.length > 1 && (
          <Tooltip placement="right" title="Sélectionner l'entreprise">
            <IconButton
              onClick={({ currentTarget }) => setCompanyMenuAnchorEl(currentTarget)}
              size="small"
            >
              {companyMenuAnchorEl ? (
                <KeyboardArrowUp fontSize="small" />
              ) : (
                <KeyboardArrowDown fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Box display="flex" flexDirection="column" flexGrow={1} gap={2}>
        <Box flexGrow={1} paddingLeft={2} paddingRight={0.5}>
          <List
            disablePadding
            component="nav"
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {displayGetStartedEmployeeLink && (
              <ListItem
                disablePadding
                secondaryAction={<ChevronRightIcon color="primary" />}
                style={{ marginTop: 8 }}
              >
                <CustomListItemButton onClick={toggle} to="/get-started">
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    <Box height={28} position="relative">
                      <CircularProgress
                        size={28}
                        sx={{ color: '#D9E7FF' }}
                        thickness={6}
                        value={100}
                        variant="determinate"
                      />
                      <CircularProgress
                        color="primary"
                        size={28}
                        sx={{ position: 'absolute', top: 0, left: 0 }}
                        thickness={6}
                        value={
                          (getStartedEmployeeProgression.doneCount /
                            getStartedEmployeeSteps.filter(
                              (key) => !getStartedEmployeeProgression.ignoredSteps[key],
                            ).length) *
                          100
                        }
                        variant="determinate"
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={t('companies.navigation.get_started')}
                    secondary={t('companies.navigation.get_started_status', {
                      count: getStartedEmployeeProgression.doneCount,
                      allCount: getStartedEmployeeSteps.filter(
                        (key) => !getStartedEmployeeProgression.ignoredSteps[key],
                      ).length,
                    })}
                    slotProps={{
                      primary: { ...primaryTypographyProps, fontWeight: 600 },
                      secondary: { color: 'primary', variant: 'caption' },
                    }}
                  />
                </CustomListItemButton>
              </ListItem>
            )}
            {displayGetStartedAdminLink && (
              <CustomListItemButton onClick={toggle} style={{ marginTop: 8 }} to="/get-started">
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <Box height={28} position="relative">
                    <CircularProgress
                      size={28}
                      sx={{ color: '#D9E7FF' }}
                      thickness={6}
                      value={100}
                      variant="determinate"
                    />
                    <CircularProgress
                      color="primary"
                      size={28}
                      sx={{ position: 'absolute', top: 0, left: 0 }}
                      thickness={6}
                      value={
                        (getStartedAdminProgression.doneCount / getStartedAdminSteps.length) * 100
                      }
                      variant="determinate"
                    />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t('companies.navigation.get_started')}
                  secondary={t('companies.navigation.get_started_status', {
                    count: getStartedAdminProgression.doneCount,
                    allCount: getStartedAdminSteps.length,
                  })}
                  slotProps={{
                    primary: { ...primaryTypographyProps, fontWeight: 600 },
                    secondary: { color: 'primary', variant: 'caption' },
                  }}
                />
                <ListItemIcon
                  className="chevron"
                  sx={{ color: 'inherit', justifyContent: 'flex-end', minWidth: 24 }}
                >
                  <ChevronRightIcon color="primary" sx={{ height: 16, width: 16 }} />
                </ListItemIcon>
              </CustomListItemButton>
            )}
            <CustomListItemButton onClick={toggle} to="/">
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <HomeOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('companies.navigation.home')}
                slotProps={{ primary: primaryTypographyProps }}
              />
              <ListItemIcon
                className="chevron"
                sx={{ color: 'inherit', justifyContent: 'flex-end', minWidth: 24 }}
              >
                <ChevronRightIcon color="primary" sx={{ height: 16, width: 16 }} />
              </ListItemIcon>
            </CustomListItemButton>
            {linkLists.map(({ key: listKey, titleKey: listTitleKey, items }) => {
              if (
                listKey === 'fmd' &&
                (partnerType === 'holding' ||
                  (employee && !employee.isAdmin && !fmdConfig) ||
                  hideFmd)
              )
                return <Fragment key={listKey} />;

              return (
                <List
                  disablePadding
                  component="div"
                  key={listKey}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    marginTop: employee?.isCollaborator ? 0 : 2,
                  }}
                >
                  {!employee?.isCollaborator && (
                    <ListSubheader
                      disableSticky
                      sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 'initial' }}
                    >
                      {t(listTitleKey).toUpperCase()}
                    </ListSubheader>
                  )}
                  {listKey === 'animation' &&
                    highlightedDrawerEvents.map((event, index) => {
                      // highlightedDrawerEvents is built by scanning highlightedEvents (ordered
                      // -start_datetime) and keeping the first event per family, so index 0 is
                      // necessarily the most recent one across both families.
                      const isMostRecent = index === 0;
                      // bfg keeps the default badge colors, mav has its own fixed palette.
                      const eventFamily = getEventFamily(event.code);
                      const badgeColors =
                        eventFamily === 'mav'
                          ? { primaryColor: '#FFE499', textPrimaryColor: '#223C5B' }
                          : {};

                      return (
                        <CustomListItemButton
                          key={event.id}
                          onClick={toggle}
                          to={`/events/${event.id}`}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                            {event.icon ? (
                              <Box
                                alignItems="center"
                                display="flex"
                                height={24}
                                justifyContent="center"
                                width={24}
                              >
                                <img
                                  alt={event.title}
                                  src={event.icon}
                                  style={{ maxHeight: '100%', maxWidth: '100%' }}
                                />
                              </Box>
                            ) : (
                              <Avatar sx={{ height: 24, width: 24 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <>
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {event.title}
                                </span>
                                {isMostRecent && (
                                  <Box
                                    alignItems="center"
                                    display="inline-flex"
                                    flexShrink={0}
                                    gap={0.5}
                                    marginLeft="auto"
                                  >
                                    <HighlightedBadge {...badgeColors} />
                                  </Box>
                                )}
                              </>
                            }
                            slotProps={{ primary: primaryTypographyProps }}
                          />
                          <ListItemIcon
                            className="chevron"
                            sx={{ color: 'inherit', justifyContent: 'flex-end', minWidth: 24 }}
                          >
                            <ChevronRightIcon color="primary" sx={{ height: 16, width: 16 }} />
                          </ListItemIcon>
                        </CustomListItemButton>
                      );
                    })}
                  {items.map(({ key, to, Icon, titleKey }) => {
                    const premium = false;
                    let restricted = false;
                    let isAlpha = false;

                    const isAlphaKey = alphaPages.includes(key);
                    if (isAlphaKey) {
                      if (!currentUser || !isAuthorized(currentUser, key)) {
                        return <Fragment key={key} />;
                      }
                      isAlpha = true;
                    }
                    if (key === 'fmdConfig') {
                      if (!permissions.fmdEnabled || !employee?.isAdmin || fmdConfig !== null)
                        return <Fragment key={key} />;
                    } else if (key === 'fmd') {
                      if (!permissions.fmdEnabled || !fmdConfig || employee?.isCollaborator)
                        return <Fragment key={key} />;
                    } else if (key === 'fmdEmployee') {
                      if (!permissions.fmdEnabled || !fmdConfig || !employee?.isCollaborator)
                        return <Fragment key={key} />;
                    } else if (key === 'fmdCertificates') {
                      if (!employee?.isAdmin || !fmdConfig) return <Fragment key={key} />;
                      if (!permissions.fmdEnabled) restricted = true;
                    } else if (key === 'fmdInfo') {
                      if (permissions.fmdEnabled || fmdConfig !== null || !employee?.isAdmin)
                        return <Fragment key={key} />;
                      restricted = true;
                    } else if (key === 'holdingOverview') {
                      if (partnerType !== 'holding' || !employee?.isAdmin)
                        return <Fragment key={key} />;
                    } else if (key === 'incitation') {
                      if (partnerType === 'holding' || !employee?.isAdminOrAnimator)
                        return <Fragment key={key} />;
                      else if (!permissions.mobilitySurveyResponsesReadEnabled) restricted = true;
                    } else if (key === 'covelotaf') {
                      if (
                        !featureFlags.covelotaf ||
                        partnerType === 'holding' ||
                        !employee?.isAdmin
                      )
                        return <Fragment key={key} />;
                    }

                    return (
                      <CustomListItemButton key={key} onClick={toggle} premium={premium} to={to}>
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                          <Icon />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <>
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {key === 'challenges' && highlightedDrawerEvents.length > 0
                                  ? t('companies.navigation.other_challenges')
                                  : t(titleKey)}
                              </span>
                              {(isAlpha || premium || restricted) && (
                                <Box
                                  alignItems="center"
                                  display="inline-flex"
                                  flexShrink={0}
                                  gap={0.5}
                                  marginLeft={0.5}
                                >
                                  {isAlpha && <AlphaBadge />}
                                  {(premium || restricted) && <PremiumAvatar size="small" />}
                                </Box>
                              )}
                            </>
                          }
                          slotProps={{ primary: primaryTypographyProps }}
                        />
                        <ListItemIcon
                          className="chevron"
                          sx={{ color: 'inherit', justifyContent: 'flex-end', minWidth: 24 }}
                        >
                          <ChevronRightIcon color="primary" sx={{ height: 16, width: 16 }} />
                        </ListItemIcon>
                      </CustomListItemButton>
                    );
                  })}
                </List>
              );
            })}
          </List>
        </Box>
        <Box display="flex" flexDirection="column" gap={2} paddingX={2}>
          <List
            disablePadding
            component="nav"
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {partnerType !== 'holding' && employee?.isAdminOrAnimator && (
              <>
                <Box position="relative">
                  <ListItemButton
                    color="inherit"
                    component={NavLink}
                    onClick={toggle}
                    sx={() => ({
                      borderRadius: 2,
                      paddingY: 0.5,
                      '.MuiListItemIcon-root': { opacity: 0.8 },
                    })}
                    to="/admin/employees"
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('companies.pages.admin.company.navigation.employees')}
                      slotProps={{ primary: { variant: 'body2' } }}
                    />
                  </ListItemButton>
                </Box>
              </>
            )}
            <ListItemButton
              color="inherit"
              onClick={({ currentTarget }) => setHelpMenuAnchorEl(currentTarget)}
              sx={() => ({
                borderRadius: 2,
                paddingY: 0.5,
                '.MuiListItemIcon-root': { opacity: 0.8 },
              })}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('companies.navigation.help')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </ListItemButton>
          </List>
          {employee?.isAdmin && partnerType === 'free' && (
            <Box
              bgcolor="#EAE2FC"
              borderRadius={2}
              display="flex"
              flexDirection="column"
              gap={1}
              padding={2}
            >
              <PremiumIcon sx={{ color: '#6236BD', height: 20, width: 20 }} />
              <Typography color="#2C2E30" variant="caption">
                Déverrouillez tout le potentiel de Geovelo Entreprise, et faites de votre entreprise
                un paradis pour vélotaffeur
              </Typography>
              <Box>
                <UpgradeOfferButton context="Drawer Card" size="small" />
              </Box>
            </Box>
          )}
          <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ListItem
              disablePadding
              secondaryAction={
                <Box height={20}>
                  <MoreHoriz fontSize="small" />
                </Box>
              }
              sx={{ '.MuiListItemSecondaryAction-root': { pointerEvents: 'none' } }}
            >
              <ListItemButton
                color="inherit"
                onClick={({ currentTarget }) => setUserMenuAnchorEl(currentTarget)}
                sx={{
                  border: '1px solid #DDE0E7',
                  borderRadius: 2,
                  minHeight: 62,
                  paddingX: 1,
                  paddingY: 0.5,
                  '.MuiListItemIcon-root': { opacity: 0.8 },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  {currentUser && employee ? (
                    currentUser?.profilePicture ? (
                      <Avatar src={currentUser.profilePicture} sx={{ height: 32, width: 32 }} />
                    ) : (
                      <Avatar
                        sx={({ palette }) => ({
                          bgcolor: '#EEF9F4',
                          color: palette.primary.main,
                          fontSize: '1.1rem',
                          height: 32,
                          width: 32,
                        })}
                      >
                        {employee.firstName || employee.lastName
                          ? [employee.firstName, employee.lastName]
                              .filter(Boolean)
                              .map((part) => part?.[0].toUpperCase())
                              .join('')
                          : currentUser.username?.[0].toUpperCase()}
                      </Avatar>
                    )
                  ) : (
                    <Skeleton height={32} variant="circular" width={32} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    currentUser && employee ? (
                      employee.name || currentUser.username
                    ) : (
                      <Skeleton variant="text" width={100} />
                    )
                  }
                  secondary={
                    employee?.isAdminOrAnimator
                      ? t(
                          employee.isAnimator
                            ? 'companies.profile_types.animator'
                            : 'companies.profile_types.admin',
                        )
                      : undefined
                  }
                  slotProps={{
                    primary: { noWrap: true, variant: 'body2' },
                    secondary: { noWrap: true, variant: 'caption' },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Box>
      <Box
        alignItems="center"
        display="flex"
        flexShrink={0}
        height={64}
        justifyContent="center"
        onDoubleClick={toggleReactQueryDevtools}
        paddingX={2}
      >
        <AppInlineIcon sx={{ height: 28, width: (28 / 35) * 261 }} />
      </Box>
      <CompanyMenu
        companyMenuAnchorEl={companyMenuAnchorEl}
        setCompanyMenuAnchorEl={setCompanyMenuAnchorEl}
      />
      <Menu
        anchorEl={helpMenuAnchorEl}
        onClose={() => setHelpMenuAnchorEl(null)}
        open={Boolean(helpMenuAnchorEl)}
        slotProps={{
          root: { sx: { '.MuiBackdrop-root': { bgcolor: 'initial' } } },
          paper: { sx: { borderRadius: 2 } },
        }}
      >
        <MenuItem
          dense
          component="a"
          href={environment.faqLinks.home}
          onClick={() => setHelpMenuAnchorEl(null)}
          rel="noreferrer"
          target="_blank"
        >
          <ListItemIcon>
            <SurveyIcon />
          </ListItemIcon>
          <ListItemText primary="FAQ" />
        </MenuItem>
        <MenuItem
          dense
          component="a"
          href={environment.driveLinks.communicationKitUrl}
          onClick={() => setHelpMenuAnchorEl(null)}
          rel="noreferrer"
          target="_blank"
        >
          <ListItemIcon>
            <FolderOpen />
          </ListItemIcon>
          <ListItemText primary={t('companies.navigation.communication_kit')} />
        </MenuItem>
        <MenuItem
          dense
          disabled={!crispInitialized}
          onClick={() => {
            showChat();
            setHelpMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <ChatOutlined />
          </ListItemIcon>
          <ListItemText primary={t('companies.navigation.contact_support')} />
        </MenuItem>
        <MenuItem
          dense
          disabled={!currentPartner}
          onClick={() => {
            setHelpMenuAnchorEl(null);
            if (currentPartner) contactAdmin(currentPartner);
          }}
        >
          <ListItemIcon>
            <MailOutline />
          </ListItemIcon>
          <ListItemText primary={t('companies.navigation.contact_admin')} />
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            setHelpMenuAnchorEl(null);
            ConsentService.openPanel();
          }}
        >
          <ListItemIcon>
            <CookieOutlined />
          </ListItemIcon>
          <ListItemText primary={t('commons.cookies_settings')} />
        </MenuItem>
      </Menu>
      <Popover
        anchorEl={userMenuAnchorEl}
        onClose={() => setUserMenuAnchorEl(null)}
        open={Boolean(userMenuAnchorEl)}
        slotProps={{
          root: { sx: { '.MuiBackdrop-root': { bgcolor: 'initial' } } },
          paper: { sx: { borderRadius: 2, minWidth: 300 } },
        }}
      >
        <Box alignItems="center" display="flex" gap={2} justifyContent="space-between" padding={2}>
          <Typography variant="caption">
            {t('companies.navigation.my_account').toUpperCase()}
          </Typography>
          {employee?.isAdminOrAnimator ? (
            <Box
              alignItems="center"
              bgcolor="#FAE3EE"
              borderRadius={1}
              display="flex"
              paddingX={1}
              paddingY={0.5}
            >
              <Typography color="#C6377C" fontWeight={700} variant="caption">
                {t(
                  employee.isAnimator
                    ? 'companies.profile_types.animator'
                    : 'companies.profile_types.admin',
                )}
              </Typography>
            </Box>
          ) : (
            <Box
              alignItems="center"
              bgcolor="#E7F4FA"
              borderRadius={1}
              display="flex"
              paddingX={1}
              paddingY={0.5}
            >
              <Typography color="#1589BB" fontWeight={700} variant="caption">
                {t('companies.profile_types.user')}
              </Typography>
            </Box>
          )}
        </Box>
        {employee?.professionalEmail && (
          <Box paddingX={2}>
            <Typography color="textSecondary" variant="body2">
              {employee.professionalEmail}
            </Typography>
          </Box>
        )}
        <List>
          {employee?.isAdmin && (
            <ListItemButton
              dense
              component={NavLink}
              onClick={() => {
                setUserMenuAnchorEl(null);
                toggle?.();
              }}
              to="/admin"
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ConfigIcon />
              </ListItemIcon>
              <ListItemText primary={t('companies.navigation.settings')} />
            </ListItemButton>
          )}
          {currentUser?.isStaff && (
            <ListItemButton
              dense
              component={NavLink}
              onClick={() => {
                setUserMenuAnchorEl(null);
                toggle?.();
              }}
              to="/tools"
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AdminPanelSettingsOutlined />
              </ListItemIcon>
              <ListItemText primary={t('companies.navigation.administration')} />
            </ListItemButton>
          )}
          <ListItemButton
            dense
            component={NavLink}
            onClick={() => {
              setUserMenuAnchorEl(null);
              toggle?.();
            }}
            to="/profile"
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary={t('companies.navigation.my_profile')} />
          </ListItemButton>
          <ListItemButton dense disabled={signingOut} onClick={handleSignOut}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('companies.navigation.sign_out')} />
          </ListItemButton>
        </List>
      </Popover>
      <RestrictAccessDialog
        onClose={() => openRestrictAccessDialog(false)}
        open={restrictAccessDialogOpen}
      />
      {adminToDisplay && (
        <ContactAdminDialog open onClose={closeAdminDialog} partner={adminToDisplay} />
      )}
    </>
  );
}

function CustomListItemButton({
  premium,
  children,
  ...props
}: ListItemButtonProps & NavLinkProps & { premium?: boolean }): React.JSX.Element {
  if (premium) {
    return (
      <Tooltip placement="right" title="Abonnez-vous pour accéder à cette fonctionnalité">
        <ListItemButton
          disableRipple
          color="inherit"
          component="div"
          sx={{
            borderRadius: 2,
            paddingY: 0.5,
            '.MuiListItemIcon-root': { opacity: 0.8 },
            '.chevron': { display: 'none' },
          }}
        >
          {children}
        </ListItemButton>
      </Tooltip>
    );
  }

  return (
    <ListItemButton
      end
      color="inherit"
      component={NavLink}
      sx={({ palette }) => ({
        borderRadius: 2,
        paddingY: 0.5,
        '&.active': {
          bgcolor: '#F3F6FF',
          color: palette.primary.main,
        },
        '&:not(.active)': {
          '.MuiListItemIcon-root': { opacity: 0.8 },
          '.chevron': { display: 'none' },
        },
      })}
      {...props}
    >
      {children}
    </ListItemButton>
  );
}

export default Drawer;
